import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Coffee, Play } from 'lucide-react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { StatusBadge } from '@/components/ui/Badge';
import { EmptyState, ErrorState, TableSkeleton } from '@/components/ui/States';
import { ScriptRunLogDialog } from '@/components/jobs/LogView';
import { formatDateTime, formatDomain, formatNumber, formatRelative } from '@/lib/format';
import { cn } from '@/lib/cn';

/**
 * Layar "Scraping Saat Ini" - hanya run yang masih hidup.
 *
 * Terpisah dari List Job karena kebutuhannya berbeda: di sini pengguna
 * memantau, bukan mencari. Karena itu tidak ada filter atau paginasi, dan
 * datanya menyegarkan dirinya sendiri tiap dua detik.
 */
export const RunningJobsPage = () => {
  const [logTerbuka, setLogTerbuka] = useState(null);
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['runs', 'active'],
    queryFn: () => api.get('/dashboard/recent-runs'),
    // Progres realtime datang lewat WebSocket; polling ini cadangan bila
    // koneksi itu terputus.
    refetchInterval: 2_000,
  });

  const runs = (data?.data ?? []).filter((r) => ['queued', 'running'].includes(r.status));

  if (isError) {
    return (
      <Card>
        <ErrorState message={error?.message} requestId={error?.requestId} onRetry={refetch} />
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-xl font-extrabold tracking-tight text-[var(--text-primary)]">
            Scraping Saat Ini
          </h2>
          <p className="mt-0.5 text-sm text-[var(--text-muted)]">
            {runs.length
              ? `${runs.length} pekerjaan sedang berjalan - halaman ini memperbarui dirinya sendiri.`
              : 'Tidak ada pekerjaan yang sedang berjalan.'}
          </p>
        </div>
        <Link to="/jobs/new">
          <Button icon={Play}>Crawl Baru</Button>
        </Link>
      </div>

      <Card className="overflow-hidden">
        {isLoading ? (
          <TableSkeleton rows={3} columns={4} />
        ) : runs.length === 0 ? (
          <EmptyState
            icon={Coffee}
            title="Semuanya tenang"
            description="Tidak ada crawl yang sedang berjalan. Begitu Anda menjalankan job, progresnya akan tampil di sini secara langsung."
            action={
              <Link to="/jobs/new">
                <Button icon={Play}>Mulai Crawl</Button>
              </Link>
            }
          />
        ) : (
          <ul className="divide-y divide-[var(--border-subtle)]">
            {runs.map((run) => {
              const percent = run.progress?.percent ?? 0;
              const job = run.jobId ?? {};

              return (
                <li key={run.id} className="px-5 py-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="min-w-0 flex-1">
                      {run.kind === 'script' ? (
                        <button
                          type="button"
                          onClick={() => setLogTerbuka(run.id)}
                          className="block max-w-full truncate text-left font-semibold text-[var(--text-primary)] hover:underline"
                        >
                          {job.name ?? 'Skrip'}
                        </button>
                      ) : (
                        <Link
                          to={`/jobs/${job._id ?? job.id ?? ''}/runs/${run.id}`}
                          className="truncate font-semibold text-[var(--text-primary)] hover:underline"
                        >
                          {job.name ?? 'Job'}
                        </Link>
                      )}
                      <p className="mt-0.5 truncate text-xs text-[var(--text-muted)]">
                        {run.kind === 'script'
                          ? 'Skrip pustaka'
                          : formatDomain(job.targetUrl)}{' '}
                        · dimulai {formatRelative(run.createdAt)}
                      </p>
                    </div>

                    <div className="flex items-center gap-4 text-sm tabular text-[var(--text-muted)]">
                      {run.kind !== 'script' && (
                        <span title="Halaman dikunjungi">
                          {formatNumber(run.progress?.pagesVisited ?? 0)}
                          {run.progress?.pagesTotal ? ` / ${run.progress.pagesTotal}` : ''} hal
                        </span>
                      )}
                      <span title="Baris terkumpul">
                        {formatNumber(run.progress?.recordsExtracted ?? 0)} baris
                      </span>
                      <StatusBadge status={run.status} size="sm" />
                    </div>
                  </div>

                  <div
                    className="mt-3 h-1.5 overflow-hidden rounded-full bg-[var(--surface-sunken)]"
                    role="progressbar"
                    aria-valuenow={percent}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={`Progres ${job.name ?? 'crawl'}`}
                  >
                    <div
                      className={cn(
                        'h-full rounded-full transition-[width] duration-500 ease-out',
                        run.status === 'queued' ? 'bg-warning-500' : 'bg-accent-600',
                      )}
                      style={{ width: `${run.status === 'queued' ? 3 : percent}%` }}
                    />
                  </div>

                  {run.progress?.currentUrl && (
                    <p className="mt-1.5 truncate font-mono text-[11px] text-[var(--text-muted)]">
                      {run.progress.currentUrl}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {logTerbuka && (
        <ScriptRunLogDialog runId={logTerbuka} onClose={() => setLogTerbuka(null)} />
      )}
    </div>
  );
};
